import React, { useMemo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import yupSchemaToFields from '../../../../../../../helpers/yupSchemaToFields';

import SidePopup from '../../../../SidePopup';
import TextInput from '../../../../../../Form/TextInput';
import FormDropdown from '../../../../../../Form/FormDropdown';
import { COUNTRY_OPTIONS } from '../../../../../../../constants/main';
import classes from './styles.module.scss';

export default function LocationSidePanel({
  isVisible,
  handleClose,
  onSave,
  initialValues,
  isEdit,
}) {
  const { t } = useTranslation();
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .trim()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        address: yup
          .string()
          .trim()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        postCode: yup.string().trim().nullable(true),
        country: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: isEdit
      ? {
          name: initialValues?.name || '',
          address: initialValues?.address || '',
          postCode: initialValues?.postCode || '',
          country:
            COUNTRY_OPTIONS.find((c) => c.value === initialValues?.country) ||
            null,
        }
      : {
          name: '',
          address: '',
          postCode: '',
          country: null,
        },
    validationSchema,
    onSubmit: (values) => {
      onSave(values);
      handleClose();
    },
    enableReinitialize: true,
  });

  const onClose = () => {
    setHasTriedToSubmit(false);
    handleClose();
  };

  const resetForm = () => {
    formik.resetForm();
    setHasTriedToSubmit(false);
  };

  // Ensure add-mode form is empty on open
  useEffect(() => {
    if (isVisible && !isEdit) {
      setHasTriedToSubmit(false);
      formik.resetForm({
        values: { name: '', address: '', postCode: '', country: null },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isEdit]);

  return (
    <SidePopup
      fields={[...yupSchemaToFields(validationSchema)]}
      formik={{ ...formik, resetForm, dirty: formik.dirty }}
      mode={isEdit ? 'edit' : 'add'}
      isVisible={isVisible}
      handleClose={onClose}
      title={
        isEdit
          ? t(
              'dashboardComponents.Console.CompanyManagement.addEdit.locationPanel.titleEdit'
            )
          : t(
              'dashboardComponents.Console.CompanyManagement.addEdit.locationPanel.titleAdd'
            )
      }
      leftButtonLabel={t('common.reset')}
      rightButtonLabel={isEdit ? t('common.save') : t('common.add')}
      onLeftButtonClick={resetForm}
      onRightButtonClick={() => {
        setHasTriedToSubmit(true);
        formik.handleSubmit();
      }}
    >
      <div className={classes.LocationSidePanel}>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Location Name'
              )}
              value={formik.values.name}
              height={50}
              touched={hasTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="name"
              error={formik.errors.name}
            />
          </div>
          <div className={classes.col}>
            <FormDropdown
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Country'
              )}
              value={formik.values.country}
              height={50}
              touched={hasTriedToSubmit}
              onBlur={formik.handleBlur}
              name="country"
              error={formik.errors.country}
              setFieldValue={formik.setFieldValue}
              options={COUNTRY_OPTIONS}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Country'
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Address'
              )}
              value={formik.values.address}
              height={50}
              touched={hasTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="address"
              error={formik.errors.address}
            />
          </div>
        </div>
      </div>
    </SidePopup>
  );
}
